import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form"
import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import * as authHandlers from "../redux/handlers"
import { isAuthenticated } from '../../../utils/auth'


const schema = yup.object().shape({
    code: yup
        .string()
        .matches(/^\d{6}$/, "**Please enter a valid 6-digit code.")
        .required("**Please enter the verification code")
});


function Verify(props) {
    const navigate = useNavigate()
    const location = useLocation();
    const { data: loginData } = location.state || {};

    const dispatch = useDispatch()
    const { loading: isLoading, error: serverError } = useSelector(state => state.auth)
    const [resendTimer, setResendTimer] = useState(30)
    const [canResend, setCanResend] = useState(false)

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
    });
    
    useEffect(() => {
        if (isAuthenticated()) {
            navigate("/admin/home", { replace: true });
            return;
        }
        if (!loginData) {
            navigate("/auth/login", { replace: true });
        }
        dispatch(authHandlers.clearErrorHandler())
    }, [loginData, navigate, dispatch]);
    
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    const onSubmitHandler = async (submitData) => {
        submitData = {
            ...loginData,
            ...submitData
        }
        const response = await dispatch(authHandlers.verifyHandler(submitData))
        if (response?.status === 200) {
            navigate("/admin/home", { replace: true });
        }
    }
    
    const handleResendOTP = async () => {
        if (!canResend) return;
        
        setCanResend(false);
        setResendTimer(30);
        
        try {
            await dispatch(authHandlers.loginHandler(loginData));
        } catch (error) {
            console.error("Resend OTP error:", error);
            setCanResend(true);
        }
    }

    const onErrorHandler = (errors) => {
        console.log("error \n", errors)
    }

    return (
        <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="w-full max-w-md px-8">
                <form onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)} className="space-y-4">
                     <div className="flex justify-between items-center mb-8">
                        <h1 className="text-6xl text-white text-center">
                            Verify
                        </h1>
                        <div className="w-11 h-11 rounded-full border border-gray-700 flex items-center justify-center">
                            <span className="text-gray-400" style={{ fontSize: '20px' }}>⚡</span>
                        </div>
                    </div>
                    
                    <div className="space-y-3">
                        <input 
                            {...register("code")} 
                            id="code" 
                            type="text"
                            maxLength="6"
                            placeholder="Code"
                            className="w-full px-4 text-white placeholder-gray-600 border border-gray-700 rounded-xl focus:outline-none focus:border-gray-600 transition-colors" 
                            style={{ 
                                backgroundColor: 'transparent',
                                height: '52px',
                                fontSize: '15px'
                            }}
                            autoComplete="one-time-code"
                        />
                        
                        {errors?.code && (
                            <p className="text-red-500 pl-1" style={{ fontSize: '14px' }}>
                                {errors.code.message}
                            </p>
                        )}
                        {serverError && !errors?.code && (
                            <p className="text-red-500 pl-1" style={{ fontSize: '14px' }}>
                                {serverError?.data?.detail?.message}
                            </p>
                        )}
                    </div>
                    
                    <div className="space-y-4">
                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full text-white font-medium rounded-xl hover:opacity-90 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                            style={{ 
                                backgroundColor: '#7c5cff',
                                height: '52px',
                                fontSize: '16px',
                            }}
                        >
                            {isLoading ? "Verifying..." : "Submit"}
                        </button>
                        
                        <div className="text-center mt-6">
                            {!canResend ? (
                                <p className="text-gray-500 text-sm">
                                    Resend OTP in <span className="text-gray-400">{resendTimer}s</span>
                                </p>
                            ) : (
                                <button 
                                    type="button"
                                    onClick={handleResendOTP}
                                    className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                                >
                                    Resend OTP
                                </button>
                            )}
                        </div>
                        
                        <div className="text-center">
                            <button 
                                type="button"
                                onClick={() => navigate("/auth/login")}
                                className="text-gray-500 text-sm hover:text-gray-400 transition-colors"
                            >
                                ← Back to login
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default Verify